import { makeAutoObservable, observable, action, runInAction } from "mobx";

export class VisualizationStore {
  charts = new Map();
  pendingWidgetByToolUseId = observable.map();
  // Tabs the user closed via the per-tab × button. The chart STAYS in `charts`
  // so its in-chat VizChip remains clickable; the chip's onClick reveals it
  // again by removing it from this set. Distinct from full destructive removal
  // (which currently has no caller — keep removeChart as the public hide API).
  hiddenChartIds = observable.set();
  activeChartId = null;
  canvasDismissed = false;
  canvasWidthPercent = 60;
  activeTab = "chart";
  // PLEX-4107: lineage (inputs + transform code + capped run log) for derived
  // artifacts, keyed by chartId. Populated from execute_code turn data.
  lineageByChartId = observable.map();
  // PLEX-4107: which detail-overlay tab CanvasPane should open when an artifact
  // is revealed — e.g. "view steps in Lineage ›" deep-links to "lineage".
  // Null = the overlay's default ("chart").
  detailTab = null;
  // Which render surface currently owns the slide-out canvas. Exactly one
  // canvas may mount: the v2 CanvasPane (pages/workspace/CanvasPane.js) and the legacy
  // VisualizationCanvas (components/canvas/VisualizationCanvas.js) both key off
  // `canvasShouldShow`, so without an owner both would render the active
  // artifact if they ever landed in the same tree. Both surfaces claim on mount
  // and release on unmount — ProjectView as "v2", VisualizationCanvas as
  // "legacy" — and canvasShouldShowFor() then hides every surface but the
  // owner (PLEX-4227). See claimCanvasSurface for why v2 outranks legacy.
  canvasSurface = null;
  // Optional accessor the live surface registers (ChatSurface) so a stale
  // VizChip can attempt on-demand hydration without threading conversation
  // turns through the markdown renderer. Non-observable — it holds a function.
  _turnsProvider = null;

  constructor() {
    makeAutoObservable(this, {
      charts: observable.shallow,
      pendingWidgetByToolUseId: observable,
      hiddenChartIds: observable,
      _turnsProvider: false,
      lineageByChartId: observable,
      // Plain (non-action) reads so their observable accesses stay tracked when
      // called from an observer render — autoAction would untrack them.
      canvasShouldShowFor: false,
      isShownInCanvas: false,
      lineageFor: false,
    });
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('plex-viz-canvas-dismissed');
        if (stored === 'true') this.canvasDismissed = true;
      } catch (_) {}
    }
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('plex-viz-canvas-width');
        const parsed = stored !== null ? parseFloat(stored) : NaN;
        if (Number.isFinite(parsed) && parsed >= 25 && parsed <= 75) {
          this.canvasWidthPercent = parsed;
        }
      } catch (_) {}
    }
  }

  revealChart(id, opts = {}) {
    if (!this.charts.has(id)) return;
    // Un-hide if the user previously closed this chart's tab via ×
    if (this.hiddenChartIds.has(id)) this.hiddenChartIds.delete(id);
    const switchingChart = this.activeChartId !== id;
    this.activeChartId = id;
    this.activeTab = "chart";
    // PLEX-4107: deep-link to a specific detail-overlay tab (e.g. "lineage")
    // when opts.tab is given; otherwise reset to "chart" only when switching to
    // a DIFFERENT artifact. Re-revealing the already-active chart (e.g. clicking
    // its sticky VizChip) leaves detailTab untouched so the user isn't kicked out
    // of the Versions/Lineage/Provenance tab they were viewing (PLEX-4107 review).
    if (opts.tab) this.detailTab = opts.tab;
    else if (switchingChart) this.detailTab = "chart";
    this.setCanvasDismissed(false);
  }

  // PLEX-4107: record/read an artifact's lineage (built-from inputs + transform
  // code + capped run log), extracted from its execute_code turn data.
  setLineage(chartId, info) {
    if (chartId) this.lineageByChartId.set(chartId, info);
  }

  lineageFor(chartId) {
    return this.lineageByChartId.get(chartId) || null;
  }

  setDetailTab(tab) {
    this.detailTab = tab || null;
  }

  openSourcesTab() {
    this.activeTab = "sources";
    this.setCanvasDismissed(false);
  }

  removeChart(id) {
    if (!this.charts.has(id)) return;
    if (this.hiddenChartIds.has(id)) return;
    const wasActive = this.activeChartId === id;
    const visibleIds = this._visibleChartIds();
    const removedIndex = visibleIds.indexOf(id);
    this.hiddenChartIds.add(id);

    if (!wasActive) return;

    const remaining = this._visibleChartIds();
    if (remaining.length === 0) {
      this.activeChartId = null;
      this.activeTab = "sources";
      return;
    }
    const fallback =
      removedIndex < remaining.length
        ? remaining[removedIndex]
        : remaining[remaining.length - 1];
    this.activeChartId = fallback;
  }

  _visibleChartIds() {
    const out = [];
    for (const id of this.charts.keys()) {
      if (!this.hiddenChartIds.has(id)) out.push(id);
    }
    return out;
  }

  get visibleCharts() {
    const out = [];
    for (const [id, chart] of this.charts.entries()) {
      if (!this.hiddenChartIds.has(id)) out.push(chart);
    }
    return out;
  }

  setActiveTab(tab) {
    if (tab !== "chart" && tab !== "sources") return;
    this.activeTab = tab;
    if (tab === "chart") {
      this.setCanvasDismissed(false);
    }
  }

  setCanvasWidthPercent(value) {
    const clamped = Math.max(25, Math.min(75, Number(value) || 60));
    this.canvasWidthPercent = clamped;
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      try {
        window.localStorage.setItem('plex-viz-canvas-width', String(clamped));
      } catch (_) {}
    }
  }

  setCanvasDismissed(value) {
    this.canvasDismissed = Boolean(value);
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      try {
        window.localStorage.setItem('plex-viz-canvas-dismissed', String(this.canvasDismissed));
      } catch (_) {}
    }
  }

  markStreamingByToolUseId = action((toolUseId, partial = {}) => {
    this.pendingWidgetByToolUseId.set(toolUseId, {
      streaming: true,
      title: partial.title ?? "",
      bytesReceived: partial.bytesReceived ?? 0,
      startedAt: Date.now(),
    });
    // Track it as active so the inline render in ConversationView picks it up,
    // but do NOT auto-open the slide-out canvas. The chat surface renders
    // artifacts inline now (2026-05-18) — the slide-out only opens via an
    // explicit user click on the inline "Expand" button (revealChart).
    this.activeChartId = `pending:${toolUseId}`;
    this.activeTab = "chart";
  });

  addChart(id, payload) {
    const { toolUseId, ...rest } = payload;
    const wasPendingActive =
      toolUseId && this.activeChartId === `pending:${toolUseId}`;
    if (toolUseId && this.pendingWidgetByToolUseId.has(toolUseId)) {
      this.pendingWidgetByToolUseId.delete(toolUseId);
    }
    const streaming = rest.streaming === true;
    this.charts.set(id, { chartId: id, streaming, ...rest });
    // Re-adding a chart implicitly un-hides it (a streaming refresh of an
    // already-closed chart should bring it back into view).
    if (this.hiddenChartIds.has(id)) this.hiddenChartIds.delete(id);
    // Advance activeChartId to the real chart. If the ghost tab was active,
    // migrate to the new chart_id; otherwise always point to the newest chart.
    if (wasPendingActive || !this.activeChartId || !this.activeChartId.startsWith("pending:")) {
      this.activeChartId = id;
    }
    // Inline render is the default surface as of 2026-05-18; do NOT auto-
    // open the slide-out canvas just because a new chart arrived. The
    // inline "Expand" button calls revealChart() to opt in.
  }

  setActive(id) {
    if (this.charts.has(id)) {
      this.activeChartId = id;
    }
  }

  setActivePending(toolUseId) {
    const pendingId = `pending:${toolUseId}`;
    if (this.pendingWidgetByToolUseId.has(toolUseId)) {
      this.activeChartId = pendingId;
      this.activeTab = "chart";
    }
  }

  clear() {
    this.charts.clear();
    this.pendingWidgetByToolUseId.clear();
    this.hiddenChartIds.clear();
    this.activeChartId = null;
    this.activeTab = "chart";
  }

  get hasAny() {
    // Only counts charts visible in the tab strip — a fully-hidden store
    // shouldn't keep the canvas open.
    if (this.charts.size === 0) return false;
    for (const id of this.charts.keys()) {
      if (!this.hiddenChartIds.has(id)) return true;
    }
    return false;
  }

  get pendingWidgetEntries() {
    const out = [];
    this.pendingWidgetByToolUseId.forEach((value, key) => {
      out.push({
        chartId: `pending:${key}`,
        toolUseId: key,
        kind: "widget",
        streaming: true,
        title: value.title || "Building widget…",
      });
    });
    return out;
  }

  get canvasShouldShow() {
    return (this.hasAny || this.pendingWidgetEntries.length > 0) && !this.canvasDismissed;
  }

  // A surface claims the canvas on mount and releases on unmount. Both surfaces
  // claim — v2 from ProjectView, legacy from VisualizationCanvas — so
  // ownership does not depend on only one of them participating.
  //
  // First claim wins, with one deliberate exception: "v2" preempts "legacy".
  // Pure first-wins makes the outcome depend on mount order, and a legacy
  // canvas leaking onto a v2 route and mounting first would then suppress the
  // v2 CanvasPane — the exact inversion this ownership exists to prevent. The
  // reverse is not allowed: legacy never takes the canvas from v2.
  claimCanvasSurface(surface) {
    if (!surface) return;
    if (this.canvasSurface === null || (surface === "v2" && this.canvasSurface === "legacy")) {
      this.canvasSurface = surface;
    }
  }

  releaseCanvasSurface(surface) {
    if (this.canvasSurface === surface) this.canvasSurface = null;
  }

  // Show-decision scoped to a surface: the base canvasShouldShow AND either no
  // surface has claimed the canvas or THIS surface owns it. Guarantees the two
  // canvas mounts are mutually exclusive (PLEX-4227). Plain method (not an
  // action) so its observable reads stay tracked in an observer render.
  canvasShouldShowFor(surface) {
    if (this.canvasSurface && this.canvasSurface !== surface) return false;
    return this.canvasShouldShow;
  }

  // True when the slide-out canvas is currently presenting `id` on its chart
  // tab. The inline transcript card consults this to avoid double-rendering the
  // same artifact inline AND in the canvas (exactly-one-surface rule).
  isShownInCanvas(id) {
    if (!id) return false;
    // Scoped to the v2 surface, not the bare canvasShouldShow: the only caller
    // is the v2 transcript (ConversationView). If legacy owned the canvas with a
    // matching id, the bare check would report "in canvas" and the inline card
    // would hide its body while the v2 canvas was not rendering at all.
    return this.canvasShouldShowFor("v2") && this.activeTab === "chart" && this.activeChartId === id;
  }

  // Register/replace the turns accessor used by resolveChart. ChatSurface wires
  // this to the displayed engine's turns; cleared on unmount.
  setTurnsProvider(fn) {
    this._turnsProvider = typeof fn === "function" ? fn : null;
  }

  // Resolve a chart a VizChip references: already present → true; otherwise try
  // to hydrate it from the registered conversation turns. Returns false when
  // unresolvable (likely a hallucinated chart_id) so the chip can go inert.
  resolveChart(id) {
    if (this.charts.has(id)) return true;
    const turns = this._turnsProvider ? this._turnsProvider() : null;
    return this.hydrateChart(id, turns);
  }

  get activeChart() {
    if (!this.activeChartId) return null;
    if (this.activeChartId.startsWith("pending:")) {
      const toolUseId = this.activeChartId.slice("pending:".length);
      const pending = this.pendingWidgetByToolUseId.get(toolUseId);
      if (pending) {
        return {
          chartId: this.activeChartId,
          toolUseId,
          kind: "widget",
          streaming: true,
          title: pending.title || "Building widget…",
          spec: undefined,
        };
      }
      return null;
    }
    return this.charts.get(this.activeChartId) ?? null;
  }

  hydrateFromTurns(turns) {
    this.clear();
    if (!Array.isArray(turns)) return;
    runInAction(() => {
      let lastId = null;
      for (const turn of turns) {
        const blocks = Array.isArray(turn?.content) ? turn.content : [];
        for (const b of blocks) {
          // New path: flat json blobs (widget / sar kinds from tool turns)
          if (b?.json) {
            const j = b.json;
            if (j?.chart_id && j?.kind && j?.shown) {
              const base = { kind: j.kind, title: j.title, streaming: false, chartId: j.chart_id };
              if (j.kind === "widget") {
                this.charts.set(j.chart_id, { ...base, spec: j.html });
              } else if (j.kind === "sar") {
                this.charts.set(j.chart_id, { ...base, spec: j.spec });
              } else {
                this.charts.set(j.chart_id, { ...base, spec: j.spec });
              }
              lastId = j.chart_id;
            }
            continue;
          }
          // Existing path: toolResult blocks (plotly / network / molecule / v1 kinds)
          const tr = b?.toolResult;
          if (!tr || !Array.isArray(tr.content)) continue;
          for (const inner of tr.content) {
            const j = inner?.json;
            if (j?.chart_id && j?.kind) {
              const base = { kind: j.kind, title: j.title, streaming: false, chartId: j.chart_id };
              if (j.kind === "widget") {
                this.charts.set(j.chart_id, { ...base, spec: j.html });
              } else if (j.kind === "sar") {
                this.charts.set(j.chart_id, { ...base, spec: j.spec });
              } else {
                this.charts.set(j.chart_id, { ...base, spec: j.spec });
              }
              lastId = j.chart_id;
            }
          }
        }
      }
      if (lastId) this.activeChartId = lastId;
    });
  }

  hydrateChart = action((id, turns) => {
    if (this.charts.has(id)) return true;
    if (!Array.isArray(turns)) return false;
    for (const turn of turns) {
      const blocks = Array.isArray(turn?.content) ? turn.content : [];
      for (const b of blocks) {
        const tr = b?.toolResult;
        if (!tr || !Array.isArray(tr.content)) continue;
        for (const inner of tr.content) {
          const j = inner?.json;
          if (j?.chart_id === id && j?.kind) {
            const base = { kind: j.kind, title: j.title, streaming: false, chartId: id };
            if (j.kind === "widget") {
              this.charts.set(id, { ...base, spec: j.html });
            } else if (j.kind === "sar") {
              this.charts.set(id, { ...base, spec: j.spec });
            } else {
              this.charts.set(id, { ...base, spec: j.spec, graph: j.graph, items: j.items });
            }
            return true;
          }
        }
      }
    }
    return false;
  });
}

export const visualizationStore = new VisualizationStore();
